import PropTypes from 'prop-types';

import dayjs, { Dayjs } from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import localizedFormat from 'dayjs/plugin/localizedFormat';
dayjs.extend(relativeTime);
dayjs.extend(localizedFormat);

import { DashboardLayout } from '../../layouts';
import Box, { BoxProps as MuiBoxProps } from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import { DataList, DataListComp } from '../../components/DataList';
import { useEffect, useState } from 'react';
import {
  Avatar,
  Button,
  CircularProgress,
  formatError,
  generateImageAssestUrl,
  getInitials,
  getSessionStartDate,
  TextField,
  windowExists,
} from '@classity-edtech/ui-base';
import { useGetPermissionQuery } from '../../services/permission';
import { useLocation, useNavigate } from 'react-router-dom';
import { useGetResponsesByFormNameQuery, useUpdateResponseMutation } from '../../services/response';
import {
  ApplicationStatus,
  CertificationCategory,
  ContactMedium,
  ContactRelationship,
  CountryCode,
  EventAction,
  EventSourceName,
  FormStatus,
  FreeFormName,
  MediaType,
  NotificationLabel,
  ProofOfAddress,
  Roles,
  ServiceName,
  SeverityLevel,
} from '@classity-edtech/platform-utils';
import { Report } from '../../services/models/response';
import { globalColours } from '@classity-edtech/ui-base/dist/style/source';
import {
  Cancel,
  Check,
  Sheet,
  UserProfile,
} from '@classity-edtech/ui-base/dist/components/Displays';
import { Typography } from '@mui/material';
import { useCurriculumQuery } from '../../services/curriculums';
import { useLanguagesQuery } from '../../services/languages';
import { updateFlashMessage } from '../../store/actions/flashMessage';
import { useAppDispatch } from '../../store/hooks';
import UserAvailability from '../../components/UserAvailability';
import { getWorkingDaysForNextYear } from '../../utils/getWorkingDaysForNextYear';
import { useCreateFreeFormEventLogMutation } from '../../services/eventLogs';
import { webPaths } from '../../routes/paths';
import { useLazyViewSignedURLQuery } from '../../services/signedUrl';
import Helmet from '../../components/Helmet';
import { useGetProfileQuery } from '../../services/profile';
import { Notification } from '../../services/models/authentication';
import { useGetMyProfileQuery } from '../../services/users';
import { detectLinkType } from '../../utils/detectLinkType';

export interface HomeProps extends MuiBoxProps {}

const defaultDate = { date: null, time: null };
const defaultDates = [defaultDate];

export const Applications = ({ className }: HomeProps) => {
  const dispatch: any = useAppDispatch();
  const limit = 3;
  const navigate = useNavigate();
  const location = useLocation();
  const { data: user } = useGetMyProfileQuery();
  const [loadingClient, setLoadingClient] = useState(true);
  const [application, setApplication] = useState<Report[]>();
  const [reportIndex, setReportIndex] = useState(-1);
  const [page, setPage] = useState(1);
  const [showAvailability, setShowAvailability] = useState(false);
  const [showReasonInput, setShowReasonInput] = useState(false);
  const [showTutorEmailInput, setShowTutorEmailInput] = useState(false);
  const [currentImagePreview, setCurrentImagePreview] = useState('');
  const [reason, setReason] = useState('');
  const [tutorEmail, setTutorEmail] = useState('');
  const [interviewLocation, setInterviewLocation] = useState('');
  const [selectedCountry, setSelectedCountry] = useState('');
  const [currentAppointmentIndex, setCurrentAppointmentIndex] = useState(0);
  const [appointmentsDate, setAppointmentsDate] =
    useState<{ date: Dayjs | null; time: Dayjs | string | null }[]>(defaultDates);
  const [viewSignedUrl, viewSignedUrlResult] = useLazyViewSignedURLQuery();
  const selectedCountryCode =
    application?.find(a => a?.label === 'country')?.value?.[0]?.country || selectedCountry;
  const {
    data: applications,
    isLoading: loadingApplications,
    isFetching: fetchingApplications,
    refetch: refetchApplications,
  } = useGetResponsesByFormNameQuery(
    {
      name: FreeFormName.ONBOARDING_TUTOR,
      page,
      limit,
      offset: (page - 1) * limit,
    },
    { skip: !user?.id },
  );

  const { data: profile } = useGetProfileQuery(
    {
      userId: user?.id,
    },
    { skip: !user?.id },
  );

  const { data: curriculum } = useCurriculumQuery({
    code: selectedCountryCode?.toLowerCase() || CountryCode.NG,
    fields: 'educationalStages,session',
    sessionStart: new Date(getSessionStartDate()).toISOString(),
  });
  const { data: languages } = useLanguagesQuery();

  const {
    data: permission,
    isLoading: loadingPermissions,
    isError: errorPermission,
  } = useGetPermissionQuery(
    {
      domain: 'www',
      method: 'GET',
      slug: location.pathname,
    },
    { skip: !user?.id || !location.pathname },
  );

  const [updateResponse, updateResponseResult] = useUpdateResponseMutation();
  const [createFreeFormEvent, createFreeFormEventResult] = useCreateFreeFormEventLogMutation();

  const tabs = [
    { label: 'New', value: ApplicationStatus.INPROGRESS },
    { label: 'Interview', value: ApplicationStatus.INTERVIEW },
    { label: 'Accepted', value: ApplicationStatus.ACCEPTED },
    { label: 'Rejected', value: ApplicationStatus.REJECTED },
  ];
  const [currentTab, setCurrentTab] = useState(tabs[0]?.value);

  const handlePageClick = (page: number) => {
    setPage(page);
  };

  const handleApplication = ({ report, id }: { report: Report[]; id: string }) => {
    setReportIndex(applications?.data?.findIndex(r => r?.id === id));
    setSelectedCountry(report?.find(r => r.label === 'country')?.value?.[0]?.country);
    setApplication(report);
  };

  const closeApplication = () => {
    setApplication(undefined);
    setShowReasonInput(false);
    setShowTutorEmailInput(false);
    setReportIndex(-1);
    setReason('');
    setTutorEmail('');
    setInterviewLocation('');
    setShowAvailability(false);
    setAppointmentsDate(defaultDates);
  };

  const handleLogout = () => {
    (windowExists as any)?.localStorage.removeItem('token');
    navigate(`${webPaths.root}`);
  };

  const handleNavigation = (direction: '1' | '-1') => {
    if (direction === '-1' && reportIndex === 0) {
      return;
    }
    if (direction === '1' && reportIndex === applications?.data?.length - 1) {
      return;
    }

    const index = direction === '-1' ? reportIndex - 1 : reportIndex + 1;

    handleApplication({
      report: applications?.data?.[index]?.report,
      id: applications?.data?.[index]?.id,
    });
  };

  const toggleShowAvailability = () => {
    setShowAvailability(value => !value);
  };

  const toggleShowReasonInput = () => {
    setShowReasonInput(value => !value);
  };

  const toggleShowTutorEmailInput = () => {
    setShowTutorEmailInput(value => !value);
  };

  const handleRejectionReason = e => {
    const target = e.target;

    setReason(target.value);
  };

  const handleTutorEmail = e => {
    const target = e.target;

    setTutorEmail(target.value);
  };

  const handleInterviewLocation = e => {
    const target = e.target;

    setInterviewLocation(target.value);
  };

  const formatFullDateTime = (date: string | { date: string; time: string }) => {
    const dateString = typeof date === 'object' && 'date' in date ? date.date : date;
    const combined = dayjs(dateString);
    return {
      date: combined.format('ddd DD MMM YYYY'),
      time: combined.format('hh:mm A'),
    };
  };

  const getViewedUrl = async (key: string) => {
    setCurrentImagePreview(key);

    const signedUrlResult = await viewSignedUrl({
      key,
    });

    window.open(signedUrlResult?.data?.viewUrl, '_blank');
  };

  let interviewDates: { correlationId: string; interviewDate: string }[] = [];

  const handleSubmissionEmail = async ({
    firstName,
    userEmail,
    notification,
    approved,
  }: {
    firstName: string;
    userEmail: string;
    approved: boolean;
    notification: Notification;
  }) => {
    const emailInput = {
      fromField: 'mail@classity.co',
      subscribers: [userEmail],
      template: approved
        ? 'email.application.approved.classity.co'
        : 'email.application.rejected.classity.co',
      markers: {
        EMAIL_HELP: 'support@classity.co',
        FIRSTNAME: firstName,
        INTERVIEWER_NAME: `${profile?.firstname} ${profile?.lastname}`,
        APPOINTMENT_DATE_1: formatFullDateTime(interviewDates?.[0]?.interviewDate)?.date,
        APPOINTMENT_DATE_2: formatFullDateTime(interviewDates?.[0]?.interviewDate)?.date,
        APPOINTMENT_TIME_1: formatFullDateTime(interviewDates?.[0]?.interviewDate)?.time,
        APPOINTMENT_TIME_2: formatFullDateTime(interviewDates?.[0]?.interviewDate)?.time,
        APPOINTMENT_LINK_1: `${(windowExists?.location as any)?.origin}/${webPaths.interviewConfirmation}/${interviewDates?.[0]?.correlationId}`,
        APPOINTMENT_LINK_2: `${(windowExists?.location as any)?.origin}/${webPaths.interviewConfirmation}/${interviewDates?.[0]?.correlationId}`,
        APPOINTMENT_LOCATION:
          detectLinkType(interviewLocation, 'html') ??
          '31A Jide Agbalaya Crescent, Chevy View Estate, Lekki, Eti-Osa, Lagos, Nigeria',
        APPOINTMENT_LENGTH: '1h 30min',
      },

      notification,
    };

    await createFreeFormEvent({
      level: SeverityLevel.CRITICAL,
      action: EventAction.EMAIL,
      source: {
        name: EventSourceName.JOB,
        service: ServiceName.WWW,
      },
      message: 'Application review Email',
      data: emailInput,
    });
  };

  const handleOnboardEvent = async ({ tutorEmail }: { tutorEmail: string }) => {
    const monthsMap = {
      '01': 0,
      '02': 1,
      '03': 2,
      '04': 3,
      '05': 4,
      '06': 5,
      '07': 6,
      '08': 7,
      '09': 8,
      '10': 9,
      '11': 10,
      '12': 11,
    };

    const tutorInput = {
      user: {
        id: application?.find(a => a?.label === 'profile')?.value?.[0]?.userId,
        roleId: Roles.TUTOR,
        email: tutorEmail,
        country: application?.find(a => a?.label === 'country')?.value?.[0]?.country?.toLowerCase(),
        knowsLanguages: application?.find(a => a?.label === 'language')?.value?.[0]?.languages,
      },
      profile: {
        id: application?.find(a => a?.label === 'profile')?.value?.[0]?.userId,
        firstname: application?.find(a => a?.label === 'profile')?.value?.[0]?.firstname,
        lastname: application?.find(a => a?.label === 'profile')?.value?.[0]?.lastname,
        title: application?.find(a => a?.label === 'profile')?.value?.[0]?.title,
        bio: application?.find(a => a?.label === 'profile')?.value?.[0]?.bio,
        avatar: {
          contentUrl: application?.find(a => a?.label === 'profile')?.value?.[0]?.avatar,
          medium: MediaType.IMAGE,
        },
        governmentIds: [
          {
            url: generateImageAssestUrl(
              application?.find(a => a?.label === 'identification')?.value?.[0]?.url,
              4,
            ),
            category: application?.find(a => a?.label === 'identification')?.value?.[0]
              ?.identification,
            countryOfOrigin: application
              ?.find(a => a?.label === 'country')
              ?.value?.[0]?.country?.toLowerCase(),
          },
        ],
        proofOfAddress: {
          url: generateImageAssestUrl(
            application?.find(a => a?.label === 'address')?.value?.[0]?.credential,
            1,
          ),
          address: {
            streetNumber:
              application?.find(a => a?.label === 'address')?.value?.[0]?.address?.streetNumber ||
              '',
            unitNumber:
              application?.find(a => a?.label === 'address')?.value?.[0]?.address?.unitNumber || '',
            streetName:
              application?.find(a => a?.label === 'address')?.value?.[0]?.address?.streetName || '',
            township:
              application?.find(a => a?.label === 'address')?.value?.[0]?.address?.township || '',
            stateOrProvince:
              application?.find(a => a?.label === 'address')?.value?.[0]?.address
                ?.stateOrProvince || '',
            postalCode:
              application?.find(a => a?.label === 'address')?.value?.[0]?.address?.postalCode || '',
            country: application
              ?.find(a => a?.label === 'country')
              ?.value?.[0]?.country?.toLowerCase(),
            digital:
              application?.find(a => a?.label === 'address')?.value?.[0]?.address?.digital || '',
            note:
              application?.find(a => a?.label === 'address')?.value?.[0]?.address?.address || '',
          },
        },
        certifications: [
          application
            ?.find(a => a?.label === 'qualifications')
            ?.value?.map(v => ({
              url: generateImageAssestUrl(v?.credential, 1),
              category: CertificationCategory.TRAINING,
              countryOfOrigin: v?.institution?.countryCode,
              issuedBy: v?.institution?.id,
              issuedByName: v?.institution?.name,
              status: !!v?.status,
              periodOfStudyFrom: new Date(
                Date.UTC(
                  parseInt(v?.from?.year),
                  monthsMap[v?.from?.month?.toString()?.padStart(2, '0')],
                ),
              ),
              periodOfStudyTo: new Date(
                Date.UTC(
                  parseInt(v?.to?.year),
                  monthsMap[v?.to?.month?.toString()?.padStart(2, '0')],
                ),
              ),
            })),
          application
            ?.find(a => a?.label === 'proficiency')
            ?.value?.map(v => ({
              url: generateImageAssestUrl(v?.credential, 2),
              category: CertificationCategory.LANGUAGE,
              countryOfOrigin: v?.institution?.countryCode,
              status: !!v?.status,
              issuedBy: v?.institution?.id,
              issuedByName: v?.institution?.name,
            })),
          application
            ?.find(a => a?.label === 'credentials')
            ?.value?.map(v => ({
              url: generateImageAssestUrl(v?.credential, 3),
              category: CertificationCategory.ACADEMIC,
              countryOfOrigin: v?.institution?.countryCode,
              issuedBy: v?.institution?.id,
              issuedByName: v?.institution?.name,
              status: !!v?.status,
              periodOfStudyFrom: new Date(
                Date.UTC(
                  parseInt(v?.from?.year),
                  monthsMap[v?.from?.month?.toString()?.padStart(2, '0')],
                ),
              ),
              periodOfStudyTo: new Date(
                Date.UTC(
                  parseInt(v?.to?.year),
                  monthsMap[v?.to?.month?.toString()?.padStart(2, '0')],
                ),
              ),
            })),
        ]?.flat(),
        experiences: [
          application
            ?.find(a => a?.label === 'experience')
            ?.value?.map(v => ({
              organizationId: v?.institution?.id,
              status: !!v?.status,
              from: new Date(
                Date.UTC(
                  parseInt(v?.from?.year),
                  monthsMap[v?.from?.month?.toString()?.padStart(2, '0')],
                ),
              ),
              to: new Date(
                Date.UTC(
                  parseInt(v?.to?.year),
                  monthsMap[v?.to?.month?.toString()?.padStart(2, '0')],
                ),
              ),
              contact: {
                name: v?.referee?.name,
                relationship: ContactRelationship.REFEREE,
                contactMediums: [
                  {
                    system: ContactMedium.EMAIL,
                    value: v?.referee?.email,
                  },
                ],
              },
            })),
        ]?.flat(),
      },
      gradeLevels: {
        roleId: Roles.TUTOR,
        userId: application?.find(a => a?.label === 'profile')?.value?.[0]?.userId,
        gradeLevels: application?.find(a => a?.label === 'gradeLevel')?.value?.[0]?.gradeLevels,
      },
    };

    await createFreeFormEvent({
      level: SeverityLevel.CRITICAL,
      action: EventAction.ONBOARD,
      source: {
        name: EventSourceName.JOB,
        service: ServiceName.WWW,
      },
      message: 'Classity Tutor Onboarding',
      data: tutorInput,
    });
  };

  const handleAppointmentEventLog = async () => {
    const eventLogInputs = appointmentsDate.map(a => ({
      level: SeverityLevel.INFO,
      action: EventAction.CREATE,
      source: {
        name: EventSourceName.EVENT,
        service: ServiceName.WWW,
      },
      message: 'Interview dates',
      data: {
        createdAt: new Date()?.toISOString(),
        interviewDate: new Date(`${dayjs(a.date).format('YYYY-MM-DD')} ${a.time}`)?.toISOString(),
        userId: applications?.data?.[reportIndex]?.reporter,
        userFirstName: applications?.data?.[reportIndex]?.report?.find(r => r.label === 'profile')
          ?.value?.[0]?.firstname,
        userLastName: applications?.data?.[reportIndex]?.report?.find(r => r.label === 'profile')
          ?.value?.[0]?.lastname,
        adminId: user?.id,
        adminEmail: user?.email,
        userEmail: applications?.data?.[reportIndex]?.user?.email,
        responseId: applications?.data?.[reportIndex]?.id,
      },
    }));

    await Promise.all(
      eventLogInputs.map(async e => {
        const event = await createFreeFormEvent(e);

        if ('data' in event) {
          interviewDates.push({
            correlationId: event?.data?.correlationId,
            interviewDate: e?.data?.interviewDate,
          });
        }
      }),
    );
  };

  const handleResponseUpdate = async ({
    status,
    rejectionReason,
  }: {
    status: ApplicationStatus;
    rejectionReason?: string;
  }) => {
    if (status === ApplicationStatus.INTERVIEW && appointmentsDate?.some(d => !d.date || !d.time)) {
      return dispatch(
        updateFlashMessage({
          open: true,
          severity: 'error',
          message: 'Select availability date!',
        }),
      );
    }
    if (status === ApplicationStatus.INTERVIEW && !interviewLocation) {
      return dispatch(
        updateFlashMessage({
          open: true,
          severity: 'error',
          message: 'Enter interview location!',
        }),
      );
    }
    if (status === ApplicationStatus.ACCEPTED && !tutorEmail) {
      return dispatch(
        updateFlashMessage({
          open: true,
          severity: 'error',
          message: 'Tutor email not provided!',
        }),
      );
    }

    const updateResponseResultData = await updateResponse({
      responseId: applications?.data?.[reportIndex]?.id,
      responseInput: {
        report: applications?.data?.[reportIndex]?.report,
        formName: FreeFormName.ONBOARDING_TUTOR,
        status: FormStatus.SUBMITTED,
        data: {
          ...applications?.data?.[reportIndex]?.data,
          applicationStatus: status,
          rejectionReason,
          interviewLocation,
        },
      },
    });

    if ('error' in updateResponseResultData) {
      const error = formatError(updateResponseResultData.error.message);
      return dispatch(
        updateFlashMessage({
          open: true,
          severity: 'error',
          message: 'An error occurred!',
        }),
      );
    }

    if ('data' in updateResponseResultData) {
      const firstName = application?.find(r => r?.label === 'profile')?.value?.[0]?.firstname;
      const userEmail = applications?.data?.[reportIndex]?.user?.email;
      if (!appointmentsDate?.some(a => !a.date || !a.time)) {
        await handleAppointmentEventLog();

        updateResponse({
          responseId: applications?.data?.[reportIndex]?.id,
          responseInput: {
            report: applications?.data?.[reportIndex]?.report,
            formName: FreeFormName.ONBOARDING_TUTOR,
            status: FormStatus.SUBMITTED,
            data: {
              ...applications?.data?.[reportIndex]?.data,
              applicationStatus: status,
              rejectionReason,
              interviewLocation,
              interviewDates,
            },
          },
        });
      }
      closeApplication();
      if (status === ApplicationStatus.ACCEPTED) {
        handleOnboardEvent({ tutorEmail });
      } else {
        handleSubmissionEmail({
          approved: status === ApplicationStatus.INTERVIEW,
          firstName,
          userEmail,
          notification: applications?.data?.[reportIndex]?.user?.preferences?.notifications?.find(
            n => n?.medium === NotificationLabel.EMAIL,
          ),
        });
      }
    }
  };

  const getCountry = {
    NG: 'Nigeria',
    US: 'United States of America',
    GB: 'United Kingdom',
    CA: 'Canada',
    ZA: 'South Africa',
    KE: 'Kenya',
    GH: 'Ghana',
  };

  const getIdentity = {
    PASSPORT: 'Academic certificate',
    DRIVER: 'Language proficiency certification',
    NATIONAL_ID: 'National ID Card',
    NIN: 'National Identification Number',
  };

  const refinedDataList = applications?.data?.filter(d => d.data?.applicationStatus === currentTab);

  const dataList = {
    header: 'Applications',
    type: 'dataList',
    tabs: tabs?.map(tab => ({
      label: tab.label,
      active: currentTab === tab?.value,
      action: () => setCurrentTab(tab?.value),
    })),
    column1Header: 'Submitted',
    column2Header: 'Updated',
    transactionButtons: refinedDataList?.map(a => {
      const firstname = a?.report?.find(r => r.label === 'profile')?.value?.[0]?.firstname;
      const lastname = a?.report?.find(r => r.label === 'profile')?.value?.[0]?.lastname;
      const title = a?.report?.find(r => r.label === 'profile')?.value?.[0]?.title;
      const fullName = `${title} ${firstname} ${lastname}`;
      const avatar = generateImageAssestUrl(
        a?.report?.find(r => r.label === 'profile')?.value?.[0]?.avatar,
        1,
      );

      return {
        column1: {
          description: fullName.trim(),
        },
        column2: { description: 'Yes' },
        column3: { description: dayjs(a?.meta?.created).fromNow() },
        isActive: a?.id === applications?.data?.[reportIndex]?.id,
        action: () => handleApplication({ report: a?.report, id: a?.id }),
        icon: (
          <Avatar
            src={avatar}
            alt={fullName}
            letter={getInitials({
              firstname: `${firstname}`,
              lastname: `${lastname}`,
            })}
            showActiveIndicator={false}
          />
        ),
      };
    }),
    newTransactionButton: {
      label: 'New application',
      disabled: true,
      action: () => {},
    },
    currentPage: page,
    numPages: applications?.pagination?.pages,
    handlePageClick,
  } as DataList;

  const userProfile = () => {
    const profile = application?.find(a => a?.label === 'profile')?.value?.[0];
    const credentials = application?.find(a => a?.label === 'credentials')?.value;
    const qualifications = application?.find(a => a?.label === 'qualifications')?.value;
    const experiences = application?.find(a => a?.label === 'experience')?.value;
    const proficiency = application?.find(a => a?.label === 'proficiency')?.value;
    const identification = application?.find(a => a?.label === 'identification')?.value;
    const address = application?.find(a => a?.label === 'address')?.value;

    return {
      profile: {
        name: `${profile?.title} ${profile?.firstname} ${profile?.lastname}`,
        src: generateImageAssestUrl(profile?.avatar, 4),
        badge: 'Tutor',
        countryCode: application
          ?.find(a => a?.label === 'country')
          ?.value?.[0]?.country?.toLowerCase(),
      },
      bio: profile?.bio,
      gradeLevels:
        application
          ?.find(a => a?.label === 'gradeLevel')
          ?.value?.[0]?.gradeLevels?.map(
            id => curriculum?.educationalStages?.find(g => g?._id === id)?.stage?.text,
          )
          ?.filter(Boolean) ?? [],
      languages: application
        ?.find(a => a?.label === 'language')
        ?.value?.[0]?.languages?.map(code => languages?.find(l => l?.code === code)?.name?.text),
      qualifications: qualifications?.map(q => ({
        title: q?.classification?.name,
        institution: q?.institution?.name || q?.institutionAddress?.name,
        date: `${dayjs(`${q?.from?.year}-${q?.from?.month}`).format('MMM, YYYY')} - ${dayjs(`${q?.to?.year}-${q?.to?.month}`).format('MMM, YYYY')}`,
        gradeLevel: q?.gradeLevel,
        files: [
          {
            action: () => getViewedUrl(q?.credential),
            isLoading:
              (viewSignedUrlResult.isLoading || viewSignedUrlResult.isFetching) &&
              currentImagePreview === q?.credential,
            fileName: q?.fileName,
          },
        ],
      })),
      experiences: experiences?.map(e => ({
        institution: e?.institution?.name || e?.institutionAddress?.name,
        referee: e?.referee?.name,
        contact: e?.referee?.email,
        date: `${dayjs(`${e?.from?.year}-${e?.from?.month}`).format('MMM, YYYY')} - ${dayjs(`${e?.to?.year}-${e?.to?.month}`).format('MMM, YYYY')}`,
      })),
      englishProficiencies: proficiency?.map(p => ({
        institution: p?.institution?.name || p?.institutionAddress?.name,
        files: [
          {
            action: () => getViewedUrl(p?.credential),
            isLoading:
              (viewSignedUrlResult.isLoading || viewSignedUrlResult.isFetching) &&
              currentImagePreview === p?.credential,
            fileName: p?.fileName,
          },
        ],
      })),
      credentials: credentials?.map(c => ({
        title: c?.classification?.name,
        institution: c?.institution?.name || c?.institutionAddress?.name,
        courseName: c?.courseName,
        classOfDegree: c?.classOfDegree,
        date: `${dayjs(`${c?.from?.year}-${c?.from?.month}`).format('MMM, YYYY')} - ${dayjs(`${c?.to?.year}-${c?.to?.month}`).format('MMM, YYYY')}`,
        files: [
          {
            action: () => getViewedUrl(c?.credential),
            isLoading:
              (viewSignedUrlResult.isLoading || viewSignedUrlResult.isFetching) &&
              currentImagePreview === c?.credential,
            fileName: c?.fileName,
          },
        ],
      })),
      identifications: identification?.map(p => ({
        title: getIdentity[p?.identification],
        files: [
          {
            action: () => getViewedUrl(p?.url),
            isLoading:
              (viewSignedUrlResult.isLoading || viewSignedUrlResult.isFetching) &&
              currentImagePreview === p?.credential,
            fileName: p?.fileName,
          },
        ],
      })),
      addresses: address?.map(p => ({
        title: ProofOfAddress[p?.credentialType],
        address: `${p?.address?.address} ${p?.address?.township}, ${p?.address?.postalCode}. ${getCountry[p?.address?.country]}`,
        files: [
          {
            action: () => getViewedUrl(p?.credential),
            isLoading:
              (viewSignedUrlResult.isLoading || viewSignedUrlResult.isFetching) &&
              currentImagePreview === p?.credential,
            fileName: p?.fileName,
          },
        ],
      })),
      tabs: [
        { label: 'Profile', active: !showAvailability, action: () => setShowAvailability(false) },
        {
          label:
            applications?.data?.[reportIndex]?.data?.applicationStatus ===
            ApplicationStatus.INTERVIEW
              ? 'Interview'
              : 'Interview',
          active: showAvailability,
          action: () => setShowAvailability(true),
        },
      ],
    };
  };

  const activeApplication = applications?.data?.filter(
    d => d.data?.applicationStatus === ApplicationStatus.INTERVIEW,
  )?.[reportIndex < 0 ? 0 : reportIndex];
  const activeProfile = activeApplication?.report?.find(a => a?.label === 'profile')?.value?.[0];
  const activeAppointmentDate = activeApplication?.data?.interviewDates?.find(
    a => a.confirmed,
  )?.interviewDate;

  const appointmentAlert = {
    profile: {
      name: `${activeProfile?.firstname} ${activeProfile?.lastname}`,
      src: generateImageAssestUrl(activeProfile?.avatar, 4),
      bio: activeProfile?.bio,
    },
    cta: {
      label: 'Start',
      action: !!(
        dayjs().isAfter(dayjs(activeAppointmentDate).subtract(10, 'minute')) &&
        dayjs().isBefore(dayjs(activeAppointmentDate).add(10, 'minute'))
      )
        ? () =>
            (windowExists as any).open(
              applications?.data?.[reportIndex]?.data?.interviewLocation,
              '_blank',
            )
        : () => {},
    },
    appointmentDate: new Date(activeAppointmentDate),
    info: 'Lorem ipsum dolor sit amet consectetur. Sit massa nullam pulvinar in lacus sit enim. Interdum eros tincidunt ut magna scelerisque fermentum tempor turpis sed. Id molestie lorem penatibus nullam massa eros eu. Quam imperdiet bibendum tincidunt pretium risus gravida eget egestas.',
    isEmptyState:
      !activeAppointmentDate || dayjs().isAfter(dayjs(activeAppointmentDate).add(10, 'minute')),
    isDisabled: !(
      dayjs().isAfter(dayjs(activeAppointmentDate).subtract(10, 'minute')) &&
      dayjs().isBefore(dayjs(activeAppointmentDate).add(10, 'minute'))
    ),
    emptyStateTitle: 'No upcoming interview',
    emptyStateDescription: 'Upcoming interview dates will show up here',
  };

  const handledDayValue = ({ value, index }: { value: Dayjs; index: number }) => {
    setAppointmentsDate(dates =>
      dates.map((d, i) => {
        if (i === index) {
          return { ...d, date: value };
        }

        return d;
      }),
    );
  };

  const handleTimeValue = ({ value, index }: { value: Dayjs | string; index: number }) => {
    setAppointmentsDate(dates =>
      dates.map((d, i) => {
        if (i === index) {
          return { ...d, time: value };
        }

        return d;
      }),
    );
  };

  const applyDateValue = () => {
    if (appointmentsDate?.[0]?.date && appointmentsDate?.[0]?.time) {
      return setCurrentAppointmentIndex(appointmentsDate.at(-1) ? appointmentsDate.length - 1 : 0);
    }
  };

  const clearDateValue = () => {
    setAppointmentsDate(appointmentsDate => {
      return [{ ...defaultDate }];
    });
  };

  useEffect(() => {
    if (
      ((permission && !permission?.isValid) || errorPermission) &&
      !(loadingApplications || fetchingApplications)
    ) {
      navigate(`/404`, { replace: true });
    }
  }, [permission, errorPermission, loadingApplications, fetchingApplications]);

  useEffect(() => {
    setLoadingClient(false);
  }, []);

  useEffect(() => {
    if (application) {
      document.getElementsByTagName('html')[0].style.overflow = 'hidden';
    } else {
      document.getElementsByTagName('html')[0].style.overflow = 'auto';
    }
  }, [application]);

  const profileResponse = application?.find(a => a.label === 'profile')?.value?.[0];
  const profileData = {
    src: generateImageAssestUrl(profileResponse?.avatar, 1),
    name: `${profileResponse?.firstname} ${profileResponse?.lastname}`,
    tabs: [
      { label: 'Profile', active: !showAvailability, action: () => setShowAvailability(false) },
      { label: 'Availability', active: showAvailability, action: () => setShowAvailability(true) },
    ],
  };

  const appointmentSelection = {
    selectionActions: appointmentsDate?.map((date, index) => ({
      dateTime: [
        '09:30',
        '10:00',
        '10:30',
        '11:00',
        '11:30',
        '12:00',
        '12:30',
        '13:00',
        '13:30',
        '14:00',
        '14:30',
        '15:00',
        '15:30',
        '16:00',
        '16:30',
        '17:00',
      ],
      dateValue: date.date,
      highlightedDays: getWorkingDaysForNextYear(),
      setDateValue: value => handledDayValue({ value, index }),
      setTimeValue: value => handleTimeValue({ value, index }),
      timeValue: date.time,
    })),
    currentAppointmentIndex,
    title: 'Select interview dates',
    actionTitle: 'Select from available dates and times',
    appointmentActions: [
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0, mt: 0, width: '100%' }}>
        <Box>
          <TextField
            label='Interview Location'
            hintText='Paste a location link e.g. Zoom, Google Maps...'
            value={interviewLocation}
            onChange={handleInterviewLocation}
            showHint
          />
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'right',
            justifyContent: 'right',
            gap: 2,
            width: '100%',
          }}
        >
          <Box
            sx={{
              p: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.5,
              cursor: 'pointer',
              color: globalColours['$global-colour-interactive-primary-default'],
              '&:hover': { color: globalColours['$global-colour-interactive-primary-hover'] },
            }}
            onClick={clearDateValue}
          >
            <Cancel sx={{ width: '12px', height: '12px' }} />
            <Typography variant='body3'>Cancel</Typography>
          </Box>
          <Box
            sx={{
              p: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.5,
              cursor: 'pointer',
              color: globalColours['$global-colour-interactive-primary-default'],
              '&:hover': { color: globalColours['$global-colour-interactive-primary-hover'] },
            }}
            onClick={applyDateValue}
          >
            <Check sx={{ width: '12px', height: '12px' }} />
            <Typography variant='body3'>Apply</Typography>
          </Box>
        </Box>
      </Box>,
    ],
  } as any;

  const extractAppointmentDetails = inputs => {
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const months = [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ];

    const grouped: { [month: string]: any[] } = {};

    const refinedInputs = inputs?.filter(input => input.date && input.time) || [];

    for (let index = 0; index < refinedInputs.length; index++) {
      const input = refinedInputs[index];

      const endDate = new Date(input?.date);

      const timeMatch = input?.time?.match(/(\d+):(\d+)/i);
      if (!timeMatch) continue;

      const [, hourStr, minuteStr] = timeMatch;
      let hour = parseInt(hourStr, 10);
      const minute = parseInt(minuteStr, 10);

      const start = new Date(endDate);
      start.setHours(hour, minute, 0, 0);

      const end = new Date(start.getTime() + 90 * 60 * 1000);

      const formatTime = (d: Date) =>
        `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;

      const appointment = {
        appointmentDay: days[start.getDay()],
        appointmentDate: String(start.getDate()).padStart(2, '0'),
        appointmentTime: `${formatTime(start)} - ${formatTime(end)}`,
        appointmentLocation:
          detectLinkType(
            interviewLocation || applications?.data?.[reportIndex]?.data?.interviewLocation,
            'react',
          ) ?? '31A Jide Agbalaya Crescent, Chevy View Estate, Lekki, Eti-Osa, Lagos, Nigeria',
        appointmentInfo: '1 hour, 30min interview',
        appointmentAvatars: [generateImageAssestUrl(profileResponse?.avatar, 1)],
        cancelAction:
          applications?.data?.[reportIndex]?.data?.applicationStatus ===
          ApplicationStatus.INPROGRESS
            ? () => {
                setAppointmentsDate([index === 0 ? defaultDate : appointmentsDate[0]]);
                setCurrentAppointmentIndex(index);
              }
            : undefined,
        isActive:
          applications?.data?.[reportIndex]?.data?.applicationStatus !==
            ApplicationStatus.INPROGRESS && input?.confirmed,

        clickAction: input?.confirmed
          ? () =>
              (windowExists as any).open(
                applications?.data?.[reportIndex]?.data?.interviewLocation,
                '_blank',
              )
          : () => {},
      };

      const monthTitle = months[start.getMonth()];
      if (!grouped[monthTitle]) {
        grouped[monthTitle] = [];
      }
      grouped[monthTitle].push(appointment);
    }

    const result = Object.entries(grouped).map(([title, appointments]) => ({
      title,
      appointments,
    }));

    return result;
  };

  return (
    <>
      <Helmet />
      <DashboardLayout
        profileDropdown={{
          memberId: profile?.ids?.find(u => u?.name === 'uid')?.value,
          logoutAction: handleLogout,
        }}
      >
        {loadingPermissions || loadingApplications || loadingClient || fetchingApplications ? (
          <Box
            sx={{
              width: '100%',
              height: '80vh',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <DataListComp dataList={dataList} appointmentAlert={appointmentAlert} />
        )}
      </DashboardLayout>
      {application && (
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            zIndex: 5,
            backgroundColor: globalColours['$global-colour-background-dimmer'],
          }}
          onClick={closeApplication}
        ></Box>
      )}
      <Sheet
        title={'Application submitted'}
        previousAction={reportIndex === 0 ? undefined : () => handleNavigation('-1')}
        nextAction={
          reportIndex === applications?.data?.length - 1 ? undefined : () => handleNavigation('1')
        }
        closeSheet={closeApplication}
        openSheet={!!application}
        bodyMaxHeight={
          applications?.data?.[reportIndex]?.data?.applicationStatus ===
          ApplicationStatus.INPROGRESS
            ? undefined
            : 'calc(90vh - 8px)'
        }
        body={
          <Box sx={{ px: { xs: 0, md: 3 } }}>
            {!showAvailability ? (
              <UserProfile {...userProfile()} />
            ) : (
              <UserAvailability
                sx={{ mb: 5 }}
                summary={
                  'Schedule a date for the applicant to confirm. An email will be sent to the applicant as well as to the Classity Team when appointment is confirmed.'
                }
                applicationData={applications?.data?.[reportIndex]?.data}
                refetchApplications={refetchApplications}
                profile={profileData}
                appointmentSelection={appointmentSelection}
                appointments={extractAppointmentDetails(
                  applications?.data?.[reportIndex]?.data?.applicationStatus ===
                    ApplicationStatus.INPROGRESS
                    ? appointmentsDate
                    : applications?.data?.[reportIndex]?.data?.interviewDates?.map(a => {
                        const dateValue =
                          typeof a?.interviewDate === 'object' && 'date' in a.interviewDate
                            ? a.interviewDate.date
                            : a?.interviewDate;
                        return {
                          date: dayjs(dateValue),
                          time: dayjs(dateValue).format('hh:mm A'),
                          confirmed: a?.confirmed,
                        };
                      }),
                )}
              />
            )}
          </Box>
        }
        footer={
          <>
            {applications?.data?.[reportIndex]?.data?.applicationStatus ===
              ApplicationStatus.INPROGRESS && (
              <>
                {!showReasonInput ? (
                  <Box
                    sx={{
                      display: 'flex',
                      gap: 1,
                      alignItems: 'center',
                      flexDirection: { xs: 'column', md: 'row' },
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        gap: 1.5,
                        ml: 'auto',
                      }}
                    >
                      <Button
                        variant='text'
                        sx={{ p: 0 }}
                        onClick={toggleShowReasonInput}
                        disableBottomFixed
                      >
                        Reject
                      </Button>
                      <Button
                        sx={{ py: 0.4, px: 1.5 }}
                        onClick={
                          showAvailability
                            ? () => handleResponseUpdate({ status: ApplicationStatus.INTERVIEW })
                            : toggleShowAvailability
                        }
                        disableBottomFixed
                        data-testid={`interview-button`}
                      >
                        <Typography variant='body3SemiBold' sx={{ textTransform: 'uppercase' }}>
                          Interview
                        </Typography>
                      </Button>
                    </Box>
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <TextField
                      sx={{ width: '100%', height: '40px' }}
                      type='text'
                      name='reason'
                      placeholder='Enter reason'
                      value={reason}
                      onChange={handleRejectionReason}
                    />

                    <Button
                      sx={{ px: 1.5, py: 1, minWidth: '88px' }}
                      size='small'
                      isLoading={
                        updateResponseResult.isLoading || createFreeFormEventResult.isLoading
                      }
                      onClick={() =>
                        handleResponseUpdate({
                          status: ApplicationStatus.REJECTED,
                          rejectionReason: reason,
                        })
                      }
                      data-testid={`submit-button`}
                      showRightIcon={false}
                      disableBottomFixed
                    >
                      <Typography variant='body3SemiBold'>Submit</Typography>
                    </Button>
                  </Box>
                )}
              </>
            )}
            {applications?.data?.[reportIndex]?.data?.applicationStatus ===
              ApplicationStatus.INTERVIEW && (
              <>
                {!showReasonInput && !showTutorEmailInput ? (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-end',
                      gap: 1.5,
                    }}
                  >
                    <Button
                      variant='text'
                      sx={{ p: 0 }}
                      onClick={toggleShowReasonInput}
                      disableBottomFixed
                    >
                      Reject
                    </Button>
                    <Button
                      sx={{ py: 0.4, px: 1.5 }}
                      onClick={toggleShowTutorEmailInput}
                      data-testid={`onboard-button`}
                      disableBottomFixed
                    >
                      <Typography variant='body3SemiBold' sx={{ textTransform: 'uppercase' }}>
                        Onboard
                      </Typography>
                    </Button>
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <TextField
                      sx={{ width: '100%', height: '40px' }}
                      type={showTutorEmailInput ? 'email' : 'text'}
                      name={showTutorEmailInput ? 'tutorEmail' : 'reason'}
                      placeholder={showTutorEmailInput ? 'Enter email' : 'Enter reason'}
                      value={showTutorEmailInput ? tutorEmail : reason}
                      onChange={showTutorEmailInput ? handleTutorEmail : handleRejectionReason}
                    />

                    <Button
                      sx={{ px: 1.5, py: 1, minWidth: '88px' }}
                      size='small'
                      isLoading={
                        updateResponseResult.isLoading || createFreeFormEventResult.isLoading
                      }
                      onClick={() =>
                        showTutorEmailInput
                          ? handleResponseUpdate({
                              status: ApplicationStatus.ACCEPTED,
                            })
                          : handleResponseUpdate({
                              status: ApplicationStatus.REJECTED,
                              rejectionReason: reason,
                            })
                      }
                      data-testid={`submit-button`}
                      showRightIcon={false}
                      disableBottomFixed
                    >
                      <Typography variant='body3SemiBold'>Submit</Typography>
                    </Button>
                  </Box>
                )}
              </>
            )}
          </>
        }
      />
    </>
  );
};

Applications.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
};

Applications.defaultProps = {
  children: null,
  className: null,
};

export default styled<any>(Applications)(
  ({ theme }) => `
`,
);
